// Peças comuns das telas de documentos de frete (épico PO1-3298): Romaneio
// manual, NFse sem notas e Nova fatura (Auditoria de Faturas). Formatação sem ICU (currency.js / moment),
// seleção de linhas com totais, estados da tabela, modal no padrão do Hub.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, ModalBody, Spinner } from "reactstrap";
import currency from "currency.js";
import moment from "moment";

import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { listarRemetentes } from "../../../services/romaneioManualApi";
import { toDateInputValue } from "../../../utils/dateOnly";

export const DATE_FORMAT = "YYYY-MM-DD";

// Período padrão = mês corrente (só uma conveniência; o usuário muda à vontade).
export const periodoMesAtual = () => ({
  de: moment().startOf("month").format(DATE_FORMAT),
  ate: moment().format(DATE_FORMAT),
});

export const fmtBRL = (v) =>
  currency(Number(v) || 0, { symbol: "R$ ", separator: ".", decimal: ",", precision: 2, formatWithSymbol: true }).format();
export const fmtPeso = (v) =>
  `${currency(Number(v) || 0, { symbol: "", separator: ".", decimal: ",", precision: 0 }).format()} kg`;
export const fmtNumber = (v) => currency(Number(v) || 0, { symbol: "", separator: ".", decimal: ",", precision: 0 }).format();
// Campos só-data (emissão, embarque): o SQL devolve meia-noite UTC e o moment(v)
// mostraria o dia anterior no fuso -3; lê só a parte AAAA-MM-DD.
export const fmtData = (v) => {
  const iso = toDateInputValue(v);
  if (!iso) return "-";
  const [ano, mes, dia] = iso.split("-");
  return `${dia}/${mes}/${ano}`;
};
// Data e hora (previsão de coleta).
export const fmtDataHora = (v) => {
  const m = v ? moment(v) : null;
  return m && m.isValid() ? m.format("DD/MM/YYYY HH:mm") : "-";
};

// Mensagem do servidor ({ message } nos 409 do contrato; { error } no resto do Hub).
export const extractError = (e, fallback) =>
  (e && e.response && e.response.data && (e.response.data.message || e.response.data.error)) ||
  (e && e.message) ||
  fallback;

export const ehConflito = (e) => Boolean(e && e.response && e.response.status === 409);

export const fieldInputStyle = {
  background: AUTOPILOT_COLORS.inputBg,
  borderColor: AUTOPILOT_COLORS.inputBorder,
  color: AUTOPILOT_COLORS.title,
  boxShadow: "none",
};

// ─── Seleção de linhas com totais (qtd / peso / valor) ──────────────────────
// Guarda o item inteiro (Map id → item) para os totais valerem entre páginas.
export function useSelecao(campoPeso = "pesoBruto", campoValor = "valorTotal") {
  const [mapa, setMapa] = useState(() => new Map());

  const has = useCallback((id) => mapa.has(String(id)), [mapa]);

  const toggle = useCallback((item) => {
    setMapa((atual) => {
      const prox = new Map(atual);
      const k = String(item.id);
      if (prox.has(k)) prox.delete(k);
      else prox.set(k, item);
      return prox;
    });
  }, []);

  // Todos os visíveis marcados → desmarca os visíveis; senão marca todos.
  const toggleTodos = useCallback((itens) => {
    setMapa((atual) => {
      const prox = new Map(atual);
      const todos = itens.length > 0 && itens.every((i) => prox.has(String(i.id)));
      itens.forEach((i) => (todos ? prox.delete(String(i.id)) : prox.set(String(i.id), i)));
      return prox;
    });
  }, []);

  const limpar = useCallback(() => setMapa(new Map()), []);

  const selecionados = useMemo(() => [...mapa.values()], [mapa]);
  const ids = useMemo(() => selecionados.map((i) => i.id), [selecionados]);
  const totais = useMemo(
    () =>
      selecionados.reduce(
        (acc, i) => ({
          qtd: acc.qtd + 1,
          peso: acc.peso + (Number(i[campoPeso]) || 0),
          valor: acc.valor + (Number(i[campoValor]) || 0),
        }),
        { qtd: 0, peso: 0, valor: 0 }
      ),
    [selecionados, campoPeso, campoValor]
  );

  return { has, toggle, toggleTodos, limpar, selecionados, ids, totais };
}

export function CheckboxTodos({ itens, selecao, label = "Selecionar todas as notas visíveis" }) {
  const visiveisMarcados = itens.filter((i) => selecao.has(i.id)).length;
  const todos = itens.length > 0 && visiveisMarcados === itens.length;
  return (
    <input
      type="checkbox"
      aria-label={label}
      checked={todos}
      disabled={!itens.length}
      ref={(el) => {
        if (el) el.indeterminate = visiveisMarcados > 0 && !todos;
      }}
      onChange={() => selecao.toggleTodos(itens)}
    />
  );
}

export function SelecaoResumo({ totais, rotulo = "nota(s)", sufixo = "selecionada(s)", children, semValor = false }) {
  return (
    <div className="docs-frete__selecao" role="region" aria-label="Resumo da seleção">
      <span>
        <b>{fmtNumber(totais.qtd)}</b> {rotulo} {sufixo}
      </span>
      {!semValor ? (
        <>
          <span>
            Peso <b>{fmtPeso(totais.peso)}</b>
          </span>
          <span>
            Valor <b>{fmtBRL(totais.valor)}</b>
          </span>
        </>
      ) : null}
      <span className="docs-frete__selecao-espaco" />
      {children}
    </div>
  );
}

// ─── Estados da tabela ───────────────────────────────────────────────────────
export function EstadoTabela({ loading, erro, vazio, textoCarregando = "Consultando...", textoVazio = "Nada encontrado." }) {
  if (erro) {
    return (
      <div className="docs-frete__estado docs-frete__estado--erro" role="alert">
        {String(erro)}
      </div>
    );
  }
  if (loading) {
    return (
      <div className="docs-frete__estado">
        <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /> {textoCarregando}
      </div>
    );
  }
  if (vazio) return <div className="docs-frete__estado">{textoVazio}</div>;
  return null;
}

export function Aviso({ tom = "info", children, role }) {
  return (
    <div className={`docs-frete__aviso docs-frete__aviso--${tom}`} role={role || (tom === "erro" ? "alert" : "status")}>
      {children}
    </div>
  );
}

// Eyebrow das telas de Despacho (Lotes, Romaneio), que existem no Flow e no
// Trackfy (PO1-3490): segue o produto ativo para não dizer "Flow" no Trackfy.
export const eyebrowDespacho = (activeProduct) => (activeProduct === "TRACKFY" ? "Trackfy · Despacho" : "Flow · Despacho");

// Pill de situação (tom: info | success | warning).
export function Pill({ tom = "info", children, title }) {
  return (
    <span className={`docs-frete__pill docs-frete__pill--${tom}`} title={title}>
      {children}
    </span>
  );
}

// ─── Listas de apoio ─────────────────────────────────────────────────────────
// Remetentes do embarcador ({ Id, label }: mesma rota dos Parâmetros de Remetente).
export function useRemetentes(empresaId) {
  const [remetentes, setRemetentes] = useState([]);
  useEffect(() => {
    let ativo = true;
    setRemetentes([]);
    if (!empresaId) return undefined;
    listarRemetentes(empresaId)
      .then((lista) => ativo && setRemetentes(lista))
      .catch(() => ativo && setRemetentes([]));
    return () => {
      ativo = false;
    };
  }, [empresaId]);
  return remetentes;
}

// Lista de apoio genérica: `carregar` devolve uma Promise de array. `chave`
// recarrega (null/undefined/"" = sem carregar, lista vazia).
export function useLista(carregar, chave = "sempre") {
  const [itens, setItens] = useState([]);
  useEffect(() => {
    let ativo = true;
    setItens([]);
    if (chave === null || chave === undefined || chave === "") return undefined;
    carregar(chave)
      .then((lista) => ativo && setItens(Array.isArray(lista) ? lista : []))
      .catch(() => ativo && setItens([]));
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);
  return itens;
}

// ─── Modal no padrão do Hub (mesmo desenho do CadastroPrazoModal) ────────────
export function ModalHub({ titulo, icone = "tim-icons icon-notes", onClose, maxWidth = 760, children }) {
  return (
    <Modal isOpen toggle={onClose} modalClassName="modal-align-top" fade={false} style={{ maxWidth }}>
      <div className="docs-frete__modal">
        <div className="docs-frete__modal-head">
          <span className="docs-frete__modal-titulo">
            <i className={icone} style={{ marginRight: 8 }} />
            {titulo}
          </span>
          <button type="button" aria-label="Fechar" className="docs-frete__modal-x" onClick={onClose}>
            ×
          </button>
        </div>
        <ModalBody className="docs-frete__modal-body">{children}</ModalBody>
      </div>
    </Modal>
  );
}
